/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Route, Switch, useParams } from 'react-router-dom';
import { RouteInterface } from '../../types/routes';
import { DataShareDownloadContainer } from '../../components/data-share/DataShareDownloadContainer';
import { DataShareScopeView, RequesterType } from '../../types/dataShare';
import { PortfolioTrackerCompanyNavigation } from './partials/PortfolioTrackerCompanyNav';
import Dashboard, { DashboardSection } from '../../components/dashboard';
import { getPortfolioCompanies } from '../../actions/portfolio-tracker';
import { InitiativeCompany } from '../../types/initiative';
import { addSiteAlert } from '../../slice/siteAlertsSlice';
import { useDispatch } from 'react-redux';
import Loader from '../../components/loader';
import { DataShareSurveyOverview } from '../../components/data-share/DataShareSurveyOverview';
import {
  PortfolioTrackerCompanySummary,
  PortfolioTrackerCompanySummaryProps
} from './partials/PortfolioTrackerCompanySummary';
import { useAppSelector } from '../../reducers';
import { PortfolioCompanyInsights } from './PortfolioCompanyInsights';
import { isViewShared } from '../../utils/dataShare';

const summaryPageUrl = '/:summaryPage(overview|downloads|environmental|social|governance|internal)?';
const basePath = '/portfolio-tracker/portfolio/:portfolioId/company/:companyId';

export const PORTFOLIO_TRACKER_COMPANY: RouteInterface = {
  id: 'portfolio_tracker_company',
  label: 'Company Profile',
  path: `${basePath}${summaryPageUrl}`,
  component: PortfolioTrackerCompanyRoute,
  appPermissionId: 'app_portfolio_tracker',
  exact: true,
  auth: true,
  parentId: 'portfolio_tracker'
}

export const PORTFOLIO_TRACKER_COMPANY_DOWNLOADS: RouteInterface = {
  id: 'portfolio_tracker_company_download',
  label: 'Downloads',
  path: `${basePath}/downloads`,
  component: PortfolioTrackerCompanyRoute,
  appPermissionId: 'app_portfolio_tracker',
  exact: true,
  auth: true,
  parentId: 'portfolio_tracker'
}

interface RouteParams {
  portfolioId: string;
  companyId: string;
  page?: string;
  pageParam?: string;
  summaryPage?: string;
  questionId?: string;
}

interface NotSharedProps {
  title: string;
  children: string | React.JSX.Element;
}

export const NotShared = ({ title, children }: NotSharedProps) => {
  return (
    <Dashboard className='mt-0'>
      <DashboardSection title={title}>
        <div className='my-5 text-center'>
          <h3>{children}</h3>
        </div>
      </DashboardSection>
    </Dashboard>
  );
};

const PortfolioTrackerCompanyHeader = (props: PortfolioTrackerCompanySummaryProps) => (
  <Dashboard hasSidebar={props.hasSidebar}>
    {props.hasSidebar ? <div className='pt-insight-virtual-sidebar d-none d-xxl-inline-block' /> : null}
    <PortfolioTrackerCompanySummary {...props} />
    <PortfolioTrackerCompanyNavigation {...props} />
  </Dashboard>
);

export function PortfolioTrackerCompanyRoute() {
  const { portfolioId, companyId, summaryPage, questionId } = useParams<RouteParams>();
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState<InitiativeCompany[]>([]);
  const company = companies.find((c) => c._id === companyId);
  const { data: portfolio } = useAppSelector((state) => state.portfolio);


  const reload = useCallback(() => {
    getPortfolioCompanies(portfolioId)
      .then((d) => setCompanies(d))
      .catch(() => {
        dispatch(
          addSiteAlert({
            content: 'There was a problem getting your portfolio information. Please refresh the page and try again',
            timeout: 5000,
          })
        );
      })
  }, [dispatch, portfolioId])

  useEffect(() => {
    reload();
  }, [portfolioId, reload]);

  if (!company || !portfolio) {
    return <Loader />;
  }

  const ptCompanyHeaderProps: PortfolioTrackerCompanySummaryProps = {
    portfolioId,
    company,
    summaryPage,
    questionId,
  };

  return (
    <div className='flex-column'>
      <Switch>
        <Route path={`${basePath}/overview/:questionId?/:index?`}>
          {(isViewShared(company, DataShareScopeView.Survey)) ? (
            <>
              <PortfolioTrackerCompanyHeader {...ptCompanyHeaderProps} />
              <DataShareSurveyOverview initiativeId={companyId} portfolioId={portfolioId} />
            </>
          ) : (
            <>
              <PortfolioTrackerCompanyHeader {...ptCompanyHeaderProps} />
              <NotShared title='Surveys'>Company has not shared Surveys</NotShared>
            </>
          )}
        </Route>
        <Route path={`${basePath}/downloads`}>
          {(isViewShared(company, DataShareScopeView.Downloads)) ? (
            <>
              <PortfolioTrackerCompanyHeader {...ptCompanyHeaderProps} />
              <DataShareDownloadContainer
                reload={reload}
                company={company}
                portfolio={portfolio}
                requesterType={RequesterType.Portfolio}
                initiativeId={companyId}
                requesterId={portfolioId}
              />
            </>
          ) : (
            <>
              <PortfolioTrackerCompanyHeader {...ptCompanyHeaderProps} />
              <NotShared title='Downloads'>Company has not shared Downloads</NotShared>
            </>
          )}
        </Route>
        <Route>
          {(isViewShared(company, DataShareScopeView.Insights)) ? (
            <>
              <PortfolioTrackerCompanyHeader hasSidebar={true} {...ptCompanyHeaderProps} />
              <div className='-mt-5'>
                <PortfolioCompanyInsights
                  className='insights-dashboard'
                  portfolio={portfolio}
                  company={company}
                  summaryPage={summaryPage} />
              </div>
            </>
          ) : (
            <>
              <PortfolioTrackerCompanyHeader {...ptCompanyHeaderProps} />
              <NotShared title='Insights'>Company has not shared Insights</NotShared>
            </>
          )}
        </Route>
      </Switch>
    </div>
  );
}
